<!-- src/components/Communications/GroupWalkiePanel.vue
     Group Walkie's one panel (docs/features/native_comms_suite_FEATURE.md Phase 4), mounted once in App.vue so the call
     keeps playing across pages: an invite when a teammate starts a call in one of your groups, then the call itself
     (members, who is talking, mute, leave). It owns the group call's audio elements, one per connected teammate. -->
<template>
  <div v-if="walkie.enabled">
    <audio
      v-for="(stream, uid) in walkie.streams"
      :key="uid"
      :ref="el => attach(el, stream)"
      autoplay
      playsinline
    ></audio>

    <div
      v-if="walkie.invite && !walkie.inCall"
      class="fixed bottom-4 right-4 z-[70] w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-emerald-200 bg-white p-4 shadow-xl"
      role="alertdialog"
      aria-label="Walkie call invite"
    >
      <div class="flex items-center gap-3">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
        >
          <Phone class="h-5 w-5 animate-pulse" />
        </div>
        <div class="min-w-0">
          <div class="truncate text-sm font-semibold text-slate-800">
            {{ walkie.invite.groupName }}
          </div>
          <div class="truncate text-xs text-slate-500">
            {{ walkie.invite.fromName }} started a Walkie call
          </div>
        </div>
      </div>
      <div class="mt-3 flex gap-2">
        <button
          class="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-50"
          :disabled="walkie.joining"
          @click="walkie.enter(walkie.invite.groupId, walkie.invite.roomId)"
        >
          Join
        </button>
        <button
          class="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          @click="walkie.dismissInvite()"
        >
          Not now
        </button>
      </div>
    </div>

    <div
      v-if="walkie.inCall"
      class="fixed bottom-4 right-4 z-[70] w-80 max-w-[calc(100vw-2rem)] rounded-2xl bg-gradient-to-b from-brand-dark-blue to-[#005a90] p-4 text-white shadow-xl"
      role="region"
      aria-label="Group Walkie call"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="min-w-0">
          <div class="truncate text-sm font-semibold">{{ walkie.call.groupName }}</div>
          <div class="text-xs text-white/75">
            Walkie · {{ walkie.call.live.length }} of {{ walkie.MAX_LIVE }} in the call
          </div>
        </div>
        <Users class="h-5 w-5 shrink-0 text-white/70" />
      </div>
      <ul class="mt-3 flex flex-wrap gap-2">
        <li
          v-for="uid in walkie.call.live"
          :key="uid"
          class="flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-xs transition"
          :class="
            walkie.talking.includes(uid)
              ? 'bg-amber-300 font-semibold text-slate-900 ring-2 ring-amber-200'
              : ''
          "
        >
          <span
            class="h-1.5 w-1.5 rounded-full"
            :class="walkie.talking.includes(uid) ? 'bg-slate-900' : 'bg-emerald-300'"
          ></span>
          {{ uid === walkie.myUid ? 'You' : walkie.names[uid] || 'Teammate' }}
        </li>
      </ul>
      <div class="mt-4 flex gap-2">
        <button
          class="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/40 px-3 py-2 text-sm font-semibold transition hover:bg-white/10"
          :class="walkie.muted ? 'bg-white text-brand-dark-blue hover:bg-white/90' : ''"
          :aria-pressed="walkie.muted"
          @click="walkie.toggleMute()"
        >
          <MicOff v-if="walkie.muted" class="h-4 w-4" />
          <Mic v-else class="h-4 w-4" />
          {{ walkie.muted ? 'Unmute' : 'Mute' }}
        </button>
        <button
          class="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
          @click="walkie.leave()"
        >
          <PhoneOff class="h-4 w-4" />
          Leave
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Phone, PhoneOff, Mic, MicOff, Users } from 'lucide-vue-next'
import { useGroupWalkieStore } from '@/stores/groupWalkieStore'

const walkie = useGroupWalkieStore()

function attach(el, stream) {
  if (el && el.srcObject !== stream) el.srcObject = stream
}
</script>
